import { useState, useEffect, useCallback, useRef } from 'react';

// Configuration (in milliseconds)
const IDLE_TIMEOUT = 15 * 60 * 1000; // 15 minutes of inactivity before warning
const WARNING_DURATION = 2 * 60 * 1000; // 2 minutes warning before auto-logout
const TOKEN_REFRESH_INTERVAL = 10 * 60 * 1000; // Refresh token every 10 minutes while active

// Events that indicate user activity
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];

/**
 * Hook to monitor user session activity and handle token refresh/timeout
 * @param {Object} options
 * @param {Function} options.onRefreshToken - Called to refresh the auth token
 * @param {Function} options.onLogout - Called when session expires
 * @param {boolean} options.isAuthenticated - Whether user is currently authenticated
 * @returns {Object} Session state and controls
 */
const useSessionMonitor = ({ onRefreshToken, onLogout, isAuthenticated }) => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(WARNING_DURATION);

  const lastActivityRef = useRef(Date.now());
  const warningTimerRef = useRef(null);
  const countdownRef = useRef(null);
  const refreshIntervalRef = useRef(null);
  const isWarningActiveRef = useRef(false);

  // Reset activity timestamp
  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();

    // If warning is showing, hide it and reset
    if (isWarningActiveRef.current) {
      isWarningActiveRef.current = false;
      setShowWarning(false);
      setTimeRemaining(WARNING_DURATION);
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    }
  }, []);

  // Handle user activity events
  const handleActivity = useCallback(() => {
    // Don't reset if warning is active (user must click "Stay Logged In")
    if (!isWarningActiveRef.current) {
      resetActivity();
    }
  }, [resetActivity]);

  // Extend session (called when user clicks "Stay Logged In")
  const extendSession = useCallback(async () => {
    console.log('🔄 Extending session...');
    try {
      if (onRefreshToken) {
        await onRefreshToken();
      }
      resetActivity();
      console.log('✅ Session extended');
    } catch (error) {
      console.error('❌ Failed to extend session:', error);
      if (onLogout) {
        onLogout();
      }
    }
  }, [onRefreshToken, onLogout, resetActivity]);

  // Start the warning countdown
  const startWarningCountdown = useCallback(() => {
    if (isWarningActiveRef.current) return;

    console.log('⚠️ Session timeout warning - starting countdown');
    isWarningActiveRef.current = true;
    setShowWarning(true);
    setTimeRemaining(WARNING_DURATION);

    countdownRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        const newTime = prev - 1000;
        if (newTime <= 0) {
          // Time's up - log out
          console.log('⏰ Session timed out - logging out');
          if (countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
          }
          if (onLogout) {
            onLogout();
          }
          return 0;
        }
        return newTime;
      });
    }, 1000);
  }, [onLogout]);

  // Check for idle timeout
  const checkIdleTimeout = useCallback(() => {
    if (!isAuthenticated || isWarningActiveRef.current) return;

    const idleTime = Date.now() - lastActivityRef.current;

    if (idleTime >= IDLE_TIMEOUT) {
      startWarningCountdown();
    }
  }, [isAuthenticated, startWarningCountdown]);

  // Proactive token refresh while user is active
  const refreshTokenIfActive = useCallback(async () => {
    if (!isAuthenticated || isWarningActiveRef.current) return;

    const idleTime = Date.now() - lastActivityRef.current;

    // Only refresh if user has been active recently (within idle timeout)
    if (idleTime < IDLE_TIMEOUT && onRefreshToken) {
      console.log('🔄 Proactive token refresh...');
      try {
        await onRefreshToken();
        console.log('✅ Token refreshed proactively');
      } catch (error) {
        console.error('❌ Proactive token refresh failed:', error);
      }
    }
  }, [isAuthenticated, onRefreshToken]);

  // Set up activity listeners and timers
  useEffect(() => {
    if (!isAuthenticated) {
      // Clean up if not authenticated
      if (warningTimerRef.current) {
        clearInterval(warningTimerRef.current);
        warningTimerRef.current = null;
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      setShowWarning(false);
      isWarningActiveRef.current = false;
      return;
    }

    // Add activity event listeners
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Check for idle timeout every 10 seconds
    warningTimerRef.current = setInterval(checkIdleTimeout, 10000);

    // Proactive token refresh interval
    refreshIntervalRef.current = setInterval(refreshTokenIfActive, TOKEN_REFRESH_INTERVAL);

    // Initialize last activity
    lastActivityRef.current = Date.now();

    return () => {
      // Clean up event listeners
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });

      // Clean up timers
      if (warningTimerRef.current) {
        clearInterval(warningTimerRef.current);
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [isAuthenticated, handleActivity, checkIdleTimeout, refreshTokenIfActive]);

  // Format time remaining for display
  const formatTimeRemaining = useCallback(() => {
    const minutes = Math.floor(timeRemaining / 60000);
    const seconds = Math.floor((timeRemaining % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, [timeRemaining]);

  return {
    showWarning,
    timeRemaining,
    formattedTimeRemaining: formatTimeRemaining(),
    extendSession,
    resetActivity,
  };
};

export default useSessionMonitor;
